"use client";
import { useState, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { CustomTooltip, Badge, BadgeProps } from "@/app/components/ui";
import { VersionModal } from "./version-modal";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { useAuth } from "@/app/hooks";
import { Version } from "@/types";
import { CONFIG_KEYS } from "@/app/store/chat";
import { cn } from "@/app/lib/utils";

const MAX_VERSION_RETRIES = 2;

type AppVersionProps = {
   className?: string;
} & BadgeProps;
export function AppVersion({
   className,
   variant = "outline",
   ...props
}: AppVersionProps) {
   const [isOpen, setIsOpen] = useState(false);
   const [currentVersion, setCurrentVersion] = useState<Pick<Version, "version">>();
   const translation = useTranslations("AppVersion");
   const { user, isLoggedIn } = useAuth();
   const retryCountRef = useRef(0);

   useEffect(() => {
      let cancelled = false;
      retryCountRef.current = 0;

      const getVersion = async () => {
         try {
            const response = await fetchWithAuth(
               api_url.config_url + "?key=" + CONFIG_KEYS.appVersion
            );
            if (!response.ok) {
               console.error("[AppVersion] Failed to fetch app version:", {
                  status: response.status
               });
               return;
            }
            const { data }: { data?: Version[] } = await response.json();
            const versionsArray = Array.isArray(data) ? data : [];
            const foundVersion = versionsArray.find((entry) => {
               const hasVersionText =
                  typeof entry?.version === "string" && entry.version.trim().length > 0;
               return !entry?.disable && hasVersionText;
            });

            if (!foundVersion && retryCountRef.current < MAX_VERSION_RETRIES) {
               retryCountRef.current++;
               await new Promise((resolve) => setTimeout(resolve, 1000));
               if (cancelled) {
                  return;
               }
               return getVersion();
            }

            if (!cancelled) {
               setCurrentVersion(
                  foundVersion ? { version: foundVersion.version } : undefined
               );
            }
         } catch (error) {
            console.error("[AppVersion] Error fetching app version:", { error });
            if (!cancelled) {
               setCurrentVersion(undefined);
            }
         }
      };

      if (isLoggedIn) {
         getVersion();
      }

      return () => {
         cancelled = true;
      };
   }, [isLoggedIn]);

   if (!currentVersion?.version) {
      return null;
   }

   return (
      <>
         <CustomTooltip
            text={user?.isAdmin ? translation("whatNew") : "version"}
            delayDuration={1000}
         >
            <Badge
               variant={variant}
               className={cn(
                  "cursor-help border text-[10px] px-1.5 py-[0.5px] whitespace-nowrap shadow-sm transition-all duration-150",
                  "border-border/70 bg-background text-muted-foreground dark:bg-muted/50 dark:border-border/60",
                  "hover:border-primary/40 hover:bg-primary/10 hover:text-primary hover:shadow-md",
                  className
               )}
               onClick={() => user?.isAdmin && setIsOpen(true)}
               {...props}
            >
               {currentVersion.version}
            </Badge>
         </CustomTooltip>

         {user?.isAdmin && (
            <VersionModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
         )}
      </>
   );
}
